import { ajustesDeVenta } from './configuracion';
import { effectiveLocationId, type DailyRevenue, type Location, type TenantSettings } from './types';

/**
 * Ventas de antes del corte de IVA (19 oct 2026).
 *
 * El panel «Anotar el día» llegó a producción el 15 oct 2026 a las 22:11 UTC:
 * desde entonces se teclea con IVA y se guarda dividido entre (1 + IVA). Antes
 * la ayuda decía «anota sin IVA», pero hubo quien metía el corte con IVA (Casa
 * Sergio): lo viejo quedó con IVA y lo nuevo sin IVA. La primera vez que abre
 * el panel, Alexia le pregunta UNA vez cómo lo anotaba; con «con IVA» se
 * dividen los días manuales anteriores al corte y se marcan con
 * `ivaCorregido`, así que repetirlo no vuelve a dividir.
 */

/** Despliegue del panel con IVA. Lo guardado antes, si iba con IVA, va mezclado. */
export const CORTE_IVA_VENTAS = '2026-10-15T22:11:49Z';

export type RespuestaIvaAntes = 'con' | 'sin';

/** Lo que contestó, guardado en el tenant para no volver a preguntar. */
export interface VentasIvaAntes {
  respuesta: RespuestaIvaAntes;
  /** ISO de cuando contestó. */
  at: string;
  /** Días divididos entre (1 + IVA) al contestar «con». */
  corregidas?: number;
}

/** GET /v1/revenues/iva-antes */
export interface EstadoIvaAntes {
  preguntar: boolean;
  /** Días que se dividirían con «con IVA». */
  dias: number;
  respuesta?: RespuestaIvaAntes;
}

/** Tecleado a mano (`manual`, o ausente en los registros más viejos), no TPV ni Excel. */
const esManual = (r: Pick<DailyRevenue, 'source'>) => !r.source || r.source === 'manual';

/**
 * IVA con el que se divide un día, o `null` si no se toca: su local anota sin
 * IVA en Configuración (`salesWithVat: false`) o no tiene IVA.
 */
export function ivaParaCorregir(r: Pick<DailyRevenue, 'locationId'>, settings: TenantSettings, locations: Location[] = []): number | null {
  const id = effectiveLocationId(r.locationId);
  const ajustes = ajustesDeVenta(settings, locations.find((l) => l.locationId === id));
  if (ajustes.salesWithVat === false) return null;
  const pct = Number(ajustes.vatPct);
  return Number.isFinite(pct) && pct > 0 ? pct : null;
}

/**
 * Los registros más viejos no llevan `source`, tampoco los importados. Una
 * importación guarda muchos días en el mismo minuto (Flavio's: 183 en 3
 * minutos); a mano se anota de uno en uno. Esos bloques no se tocan.
 */
const BLOQUE_IMPORTADO = 5;
const minuto = (r: Pick<DailyRevenue, 'updatedAt'>) => (r.updatedAt ?? '').slice(0, 16);

/** Días manuales de antes del corte, sin corregir, de locales que anotan con IVA. */
export function ventasPorCorregirIva(revenues: DailyRevenue[], settings: TenantSettings, locations: Location[] = []): DailyRevenue[] {
  const sinSourcePorMinuto = new Map<string, number>();
  for (const r of revenues) if (!r.source) sinSourcePorMinuto.set(minuto(r), (sinSourcePorMinuto.get(minuto(r)) ?? 0) + 1);
  const importado = (r: DailyRevenue) => !r.source && (sinSourcePorMinuto.get(minuto(r)) ?? 0) >= BLOQUE_IMPORTADO;
  return revenues.filter((r) =>
    esManual(r)
    && !importado(r)
    && !r.ivaCorregido
    && Boolean(r.updatedAt)
    && r.updatedAt < CORTE_IVA_VENTAS
    && ivaParaCorregir(r, settings, locations) != null);
}

const r4 = (v: number) => Math.round(v * 10000) / 10000;
const dividir = (v: number | undefined, f: number) => (typeof v === 'number' && Number.isFinite(v) ? r4(v / f) : v);
const dividirMapa = (m: Record<string, number> | undefined, f: number) =>
  m ? Object.fromEntries(Object.entries(m).map(([k, v]) => [k, dividir(v, f) as number])) : m;

/** Los importes del día sin IVA (los recuentos no cambian) y la marca `ivaCorregido`. */
export function corregirIvaDia(r: DailyRevenue, vatPct: number, at = new Date()): DailyRevenue {
  const f = 1 + vatPct / 100;
  return {
    ...r,
    inHouse: dividirMapa(r.inHouse, f) as DailyRevenue['inHouse'],
    delivery: dividirMapa(r.delivery, f) ?? {},
    takeaway: dividir(r.takeaway, f),
    courtesies: dividir(r.courtesies, f),
    discounts: dividir(r.discounts, f),
    ivaCorregido: `${at.toISOString()} ÷${f}`,
  };
}

/** ¿Se le pregunta? Solo si no contestó y tiene algo que corregir. */
export function estadoIvaAntes(
  respuesta: VentasIvaAntes | undefined,
  revenues: DailyRevenue[],
  settings: TenantSettings,
  locations: Location[] = [],
): EstadoIvaAntes {
  if (respuesta) return { preguntar: false, dias: 0, respuesta: respuesta.respuesta };
  const dias = ventasPorCorregirIva(revenues, settings, locations).length;
  return { preguntar: dias > 0, dias };
}
